import { motion } from "framer-motion";
import { ArrowRight, Phone, Star } from "@phosphor-icons/react";
import Logo from "@/components/site/Logo";
import { useI18n } from "@/context/I18nContext";
import useSiteSettings from "@/hooks/useSiteSettings";

export default function Hero() {
    const { t } = useI18n();
    const { settings } = useSiteSettings();
    const heroUrl = settings.hero_image_url;
    const overlay = settings.hero_overlay_opacity ?? 0.55;
    const isOpen = settings.is_open;
    return (
        <section
            data-testid="hero"
            className="relative grain min-h-[88vh] md:min-h-screen overflow-hidden bg-[#0A0A0A] text-white"
        >
            <img
                src={heroUrl}
                alt="J&W Deli — La esquina del Sabor"
                className="absolute inset-0 w-full h-full object-cover opacity-60"
                loading="eager"
            />
            <div
                className="absolute inset-0"
                style={{
                    background: `linear-gradient(to bottom, rgba(0,0,0,${overlay * 1.1}), rgba(0,0,0,${overlay * 0.7}), rgba(0,0,0,${Math.min(0.95, overlay * 1.5)}))`,
                }}
            />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(232,120,17,0.4),transparent_55%)]" />

            <div className="relative mx-auto w-full max-w-7xl px-4 md:px-8 pt-20 md:pt-28 pb-24 flex flex-col min-h-[88vh] md:min-h-screen">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="mb-6"
                >
                    <Logo
                        size={92}
                        className="rounded-full bg-white/95 p-1.5 shadow-2xl ring-2 ring-secondary/40"
                    />
                </motion.div>

                <motion.div
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
                    className="flex items-center gap-3 mb-6"
                >
                    <span
                        className={`relative inline-flex h-3 w-3 rounded-full ${
                            isOpen ? "bg-emerald-400 pulse-dot text-emerald-400" : "bg-red-500"
                        }`}
                    />
                    <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/70">
                        {isOpen ? t("hero.badge_open") : "Closed · See hours below"}
                    </span>
                </motion.div>

                <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.7 }}
                    className="text-sm md:text-base font-bold tracking-[0.25em] uppercase text-secondary mb-4"
                >
                    {t("hero.kicker")}
                </motion.p>

                <motion.h1
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.15, duration: 0.8 }}
                    className="font-display font-black uppercase leading-[0.86] tracking-tighter text-balance text-5xl sm:text-6xl md:text-8xl lg:text-[10rem]"
                >
                    {t("hero.title_1")}
                    <span className="block text-primary">{t("hero.title_2")}</span>
                </motion.h1>

                <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.7 }}
                    className="mt-8 max-w-xl text-base md:text-lg text-white/80 leading-relaxed"
                >
                    {t("hero.subtitle")}
                </motion.p>

                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.45, duration: 0.7 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        href="#menu"
                        data-testid="hero-order-now"
                        className="group inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full pl-7 pr-3 py-3 font-bold uppercase tracking-wide text-sm transition-all hover:-translate-y-0.5"
                    >
                        {t("cta.order")}
                        <span className="grid place-items-center h-9 w-9 rounded-full bg-white text-primary group-hover:rotate-45 transition-transform">
                            <ArrowRight size={16} weight="bold" />
                        </span>
                    </a>
                    <a
                        href="tel:+12034666226"
                        data-testid="hero-call-now"
                        className="inline-flex items-center gap-2 border-2 border-white/80 hover:bg-white hover:text-black text-white rounded-full px-7 py-3 font-bold uppercase tracking-wide text-sm transition-all"
                    >
                        <Phone size={16} weight="bold" />
                        {t("cta.call")}
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.7 }}
                    className="mt-auto pt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10"
                >
                    {[
                        { k: "5.0", l: "Google rating", icon: <Star size={16} weight="fill" /> },
                        { k: "12+", l: "Family recipes" },
                        { k: "405", l: "Foxon Blvd, NH" },
                        { k: "12–8", l: "Open daily" },
                    ].map((s) => (
                        <div key={s.l} className="pt-6">
                            <div className="flex items-center gap-2 font-display text-3xl md:text-4xl font-black">
                                {s.k}
                                {s.icon && <span className="text-secondary">{s.icon}</span>}
                            </div>
                            <p className="mt-1 text-xs uppercase tracking-widest text-white/60">
                                {s.l}
                            </p>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
